import { AppsService } from '@/common/apps/apps.service';
import { post as gcsPost } from '@/data/gcs-service.js';
import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import IdeaPollsCountAggregation from '@/voc/aggregations/idea-polls-count/idea-polls-count.aggregation';
import VocItemListWithSourceIdentifierAggregation from '@/voc/aggregations/voc-item-list-with-source-identifier/voc-item-list-with-source-identifier.aggregation';
import VocItemSuggestionsForSingleItemAggregation from '@/voc/aggregations/voc-item-suggestions-for-single-item/voc-item-suggestions-for-single-item.aggregation';
import VocItemAggregation from '@/voc/aggregations/voc-item/voc-item.aggregation';
import VocItemsCountAggregation from '@/voc/aggregations/voc-items-count/voc-items-count.aggregation';
import VocItemsAggregation from '@/voc/aggregations/voc-items/voc-items.aggregation';
import { getUpdateEmail, getVocItemEmailUpdates } from '@/voc/api/emails.api';
import { createCSVBucket, processCSV } from '@/voc/api/import.api';
import {
    addAttachmentToVocItem,
    createAttachmentMedia,
    createVocItemMedia,
    deleteAttachment,
    fetchAttachmentUrl,
    fetchBannerImageUrl,
    updateBannerImageAltText,
} from '@/voc/api/media.api';
import {
    addVote,
    bulkLinkFeedbackToIdea,
    createVocItem,
    deleteVocItem,
    fetchInitialFormData,
    fetchVocItemData,
    linkFeedbackToIdea,
    loadAllVocItems,
    rejectSuggestion,
    revokeVote,
    unlinkFeedbackFromIdea,
    unlinkInsightFromIdea,
    updateVocItem,
} from '@/voc/api/voc-items.api';
import { downloadImage } from '@/voc/helpers/common.helper';
import { EXPLORE_STATUSES, getExploreStatus } from '@/voc/helpers/permissions.helper';
import { useVocItems } from '@/voc/use/use-voc-items.js';
import { UploadQueue } from '@/vuex/modules/voc/helpers/image-upload-queue.helper';
import { $canSub } from '@pendo/permissions';
import { EventBus } from '@utils/event-bus';
import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import values from 'lodash/values';
import { v4 as uuid } from 'uuid';

const { CAPTURE, VALIDATE } = SUB_PRODUCT_AREAS || {};

const ImageUploadQueue = new UploadQueue();

const vocItems = {
    namespaced: true,
    state: {
        error: {},
        formData: {},
        fetching: false,
        blade: {
            activeItems: {},
            activeItemSuggestedItems: [],
            openVotesTab: false,
            openLinkedIdeasTab: false,
            goToSuggestions: false,
            voteFilter: null,
            openingOnTopOfStackedBlade: false,
            openingOnTopOfDifferentBlade: false,
            leaveStackedBladeOpen: false,
            activeItemPromotedRoadmapItems: [],
            openedFromRoadmap: false,
            hasBeenPromotedToCurrentRoadmap: false,
            persistOpenItem: true,
            selectedCommentId: null,
        },
        vocItemCount: null,
        vocItemCountType: null,
        ideaPollCount: null,
        activeBannerImage: '',
        activeBannerImageAltText: '',
        attachmentList: [],
        createDrawerOpen: false,
        addVoteModalOpen: false,
        bladeChangeTrigger: 0,
        map: {},
        loadingLinkedIdeas: false,
        loadingLinkedJira: false,
        loadingSuggestedIdeas: false,
        allIdeas: [],
        context: '',
        loadingBladeItem: false,
        promises: {},
        lastVocItemUpdateEmail: {},
        responsesAlreadySentToFeedback: new Map(),
        openNPSBlade: false,
        showStatusModalForCurrentSession: true,
        isBulkActionInProgress: false,
    },
    mutations: {
        setFetching (state, { isFetching }) {
            state.fetching = isFetching;
        },
        setFormData (state, { type, formData }) {
            state.formData[type] = formData;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setBlade (
            state,
            {
                activeItems = {},
                activeItemSuggestedItems = [],
                openVotesTab = false,
                openLinkedIdeasTab = false,
                goToSuggestions = false,
                voteFilter = null,
                openingOnTopOfStackedBlade = false,
                openingOnTopOfDifferentBlade = false,
                leaveStackedBladeOpen = false,
                activeItemPromotedRoadmapItems = [],
                openedFromRoadmap = false,
                hasBeenPromotedToCurrentRoadmap = false,
                persistOpenItem = true,
                selectedCommentId = null,
            },
        ) {
            state.blade = {
                activeItems,
                activeItemSuggestedItems,
                openVotesTab,
                openLinkedIdeasTab,
                goToSuggestions,
                voteFilter,
                openingOnTopOfStackedBlade,
                openingOnTopOfDifferentBlade,
                leaveStackedBladeOpen,
                activeItemPromotedRoadmapItems,
                openedFromRoadmap,
                hasBeenPromotedToCurrentRoadmap,
                persistOpenItem,
                selectedCommentId,
            };
        },
        updateAssociationsInMap (state, { createdItem }) {
            Object.entries(state.map).forEach(([key, value]) => {
                createdItem.associatedItemIds.forEach((associatedItemId) => {
                    if (associatedItemId === key) {
                        if (!value.associatedItemIds) {
                            value.associatedItemIds = [];
                        }
                        if (!value.associatedItemsExpanded) {
                            value.associatedItemsExpanded = [];
                        }
                        value.associatedItemIds.push(createdItem.id);
                        value.associatedItemsExpanded.push(createdItem);
                    }
                });
            });
        },
        updateBladeActiveVocItem (state, { changes = null }) {
            const activeItem = values(state.blade.activeItems).find((item) => item.isVisible);
            const updatedItem = Object.assign(activeItem, changes);
            state.blade.activeItems = {
                ...state.blade.activeItems,
                [updatedItem.type]: updatedItem,
            };
        },
        setVocItemCount (state, { vocItemCount }) {
            state.vocItemCount = vocItemCount;
        },
        setVocItemCountType (state, { type }) {
            state.vocItemCountType = type;
        },
        setIdeaPollCount (state, { ideaPollCount }) {
            state.ideaPollCount = ideaPollCount;
        },
        incrementVocItemCount (state) {
            state.vocItemCount++;
        },
        reduceVocItemCount (state) {
            state.vocItemCount--;
        },
        setActiveBannerImage (state, { bannerImage, altText = '' }) {
            state.activeBannerImage = bannerImage;
            state.activeBannerImageAltText = altText;
        },
        setActiveBannerImageAltText (state, { altText }) {
            state.activeBannerImageAltText = altText;
        },
        setCreateDrawerOpen (state, { createDrawerOpen }) {
            state.createDrawerOpen = createDrawerOpen;
        },
        setAddVoteModalOpen (state, { addVoteModalOpen }) {
            state.addVoteModalOpen = addVoteModalOpen;
        },
        incrementBladeChangeTrigger (state) {
            state.bladeChangeTrigger++;
        },
        setMap (state, { map }) {
            state.map = map;
        },
        addToAttachmentList (state, { fileData }) {
            state.attachmentList.push(fileData);
        },
        updateAttachmentList (state, { fileData }) {
            state.attachmentList = state.attachmentList.map((attachment) => {
                if (attachment.id === fileData.id) {
                    attachment = Object.assign({}, attachment, fileData);

                    if (fileData.newId) {
                        attachment.id = fileData.newId;
                    }
                }

                return attachment;
            });
        },
        removeAttachmentFromList (state, { id }) {
            state.attachmentList = state.attachmentList.filter((fileData) => fileData.id !== id);
        },
        resetAttachmentList (state) {
            state.attachmentList = [];
        },
        setLoadingLinkedIdeas (state, { isLoading }) {
            state.loadingLinkedIdeas = isLoading;
        },
        setLoadingLinkedJira (state, { isLoading }) {
            state.loadingLinkedJira = isLoading;
        },
        setActiveSuggestions (state, { suggestions }) {
            state.blade.activeItemSuggestedItems = suggestions;
            if (state.context === 'feedback' && state.blade.activeItems.feedback) {
                state.blade.activeItems.feedback.numOfSuggestions = suggestions.length;
            }
        },
        setLoadingSuggestions (state, { loading }) {
            state.loadingSuggestedIdeas = loading;
        },
        setAllIdeas (state, { vocItems }) {
            state.allIdeas = vocItems;
        },
        setBladeActiveItems (state, { activeItems }) {
            state.blade.activeItems = activeItems;
        },
        setContext (state, { context }) {
            state.context = context;
        },
        setLoadingBladeItem (state, { loading }) {
            state.loadingBladeItem = loading;
        },
        updateBladeHiddenVocItemLinkedItems (state, { linkedItem, lastUpdatedAt }) {
            const hiddenItem = values(state.blade.activeItems).find((item) => !item.isVisible);
            const associatedItemIds = hiddenItem.associatedItemIds
                ? [...hiddenItem.associatedItemIds, linkedItem.id]
                : [linkedItem.id];
            const associatedItemsExpanded = hiddenItem.associatedItemsExpanded
                ? [...hiddenItem.associatedItemsExpanded, linkedItem]
                : [linkedItem];
            const updatedItem = Object.assign(hiddenItem, {
                associatedItemIds,
                associatedItemsExpanded,
                lastUpdatedAt,
            });
            state.blade.activeItems = {
                ...state.blade.activeItems,
                [updatedItem.type]: updatedItem,
            };
        },
        updateLinkedItemsInMap (state, { id, linkedItem, lastUpdatedAt }) {
            state.map[id].associatedItemIds = state.map[id].associatedItemIds
                ? [...state.map[id].associatedItemIds, linkedItem.id]
                : [linkedItem.id];
            state.map[id].associatedItemsExpanded = state.map[id].associatedItemsExpanded
                ? [...state.map[id].associatedItemsExpanded, linkedItem]
                : [linkedItem];
            state.map[id].lastUpdatedAt = lastUpdatedAt;
        },
        setActiveItemPromotedRoadmapItems (state, activeItemPromotedRoadmapItems) {
            state.blade.activeItemPromotedRoadmapItems = activeItemPromotedRoadmapItems;
        },
        setPromise (state, { promise, key }) {
            set(state.promises, key, promise);
        },
        updateItem (state, { item }) {
            state.map[item.id] = item;
            if (state.blade.activeItems[item.type]?.id === item.id) {
                state.blade.activeItems = {
                    ...state.blade.activeItems,
                    [item.type]: item,
                };
            }
        },
        setLastVocItemUpdateEmail (state, lastVocItemUpdateEmail) {
            state.lastVocItemUpdateEmail = lastVocItemUpdateEmail;
        },
        addResponseToResponsesAlreadySentToFeedback (state, { sourceIdentifier, vocItemId }) {
            state.responsesAlreadySentToFeedback.set(sourceIdentifier, vocItemId);
            state.responsesAlreadySentToFeedback = new Map(state.responsesAlreadySentToFeedback);
        },
        removeResponseFromResponsesAlreadySentToFeedback (state, { sourceIdentifier }) {
            state.responsesAlreadySentToFeedback.delete(sourceIdentifier);
            state.responsesAlreadySentToFeedback = new Map(state.responsesAlreadySentToFeedback);
        },
        setOpenNPSBlade (state, value) {
            state.openNPSBlade = value;
        },
        setShowStatusModalForCurrentSession (state, value) {
            state.showStatusModalForCurrentSession = value;
        },
        setIsBulkActionInProgress (state, value) {
            state.isBulkActionInProgress = value;
        },
    },
    actions: {
        async fetchVocItems (
            { state, commit, dispatch, rootState },
            { type = 'idea', filters, revenue = false, store = true },
        ) {
            if (state.promises.vocItems) {
                return state.promises.vocItems;
            }

            if (revenue) {
                await dispatch('subscription/loadSetting', { setting: 'revenue' }, { root: true }).catch(
                    (error) => error,
                );
            }

            const revenueField = revenue ? get(rootState.subscription.settings, 'revenue.value', null) : null;
            let accountTypeField = get(rootState.subscription.settings, 'accountType.value', null);
            if (!accountTypeField) {
                await dispatch('subscription/loadSetting', { setting: 'accountType' }, { root: true }).catch(
                    (error) => error,
                );
                accountTypeField = get(rootState.subscription.settings, 'accountType.value', null);
            }

            const promise = runAggregationOnce(VocItemsAggregation, {
                ...filters,
                revenueField,
                accountTypeField,
                type,
            })
                .then((vocItems) => {
                    if (store) {
                        dispatch('setVocItems', {
                            vocItems,
                            type,
                        });
                    }

                    return vocItems;
                })
                .catch((error) => {
                    throw error;
                })
                .finally(() => {
                    commit('setPromise', { key: 'vocItems', promise: null });
                });
            commit('setPromise', { key: 'vocItems', promise });

            return promise;
        },
        async openBladeForItem (
            { state, commit, dispatch, getters, rootState, rootGetters },
            {
                vocItemId,
                roadmapItemId,
                openVotesTab = false,
                openLinkedIdeasTab = false,
                goToSuggestions = false,
                voteFilter = null,
                openingOnTopOfStackedBlade = false,
                openingOnTopOfDifferentBlade = false,
                openedFromRoadmap = false,
                hasBeenPromotedToCurrentRoadmap = false,
                persistOpenItem = true,
                selectedCommentId = null,
                type = 'idea',
                isExploreSetup = false,
            },
        ) {
            const { addVocItem, getVocItemById, cleanupVocItems } = useVocItems({ type });
            try {
                let addToMap = true;
                let newItem;
                const vocItemFromStore = state.map[vocItemId] || getVocItemById(vocItemId);
                if (!vocItemFromStore) {
                    commit('setLoadingBladeItem', { loading: true });
                    const revenueField = get(rootState.subscription.settings, 'revenue.value', null);
                    const accountDisplayName = get(rootState.subscription.settings, 'accountDisplayName.value', null);
                    VocItemAggregation.subProductArea = type === 'idea' ? VALIDATE : CAPTURE;
                    if (state.promises[`vocItem-${vocItemId}`]) {
                        newItem = await state.promises[`vocItem-${vocItemId}`];
                    } else {
                        const promise = runAggregationOnce(VocItemAggregation, {
                            vocItemId,
                            type,
                            segmentId: type === 'idea' ? rootState.globalFilters.selectedSegmentId : null,
                            allTime: type === 'idea' ? rootGetters['globalFilters/allTime'] : true,
                            timeSeriesConfig: type === 'idea' ? rootGetters['globalFilters/timeSeriesSpec'] : null,
                            revenueField,
                            accountDisplayName,
                            expandInsights: isExploreSetup,
                        })
                            .then(async (item) => {
                                if (isEmpty(item)) {
                                    addToMap = false;

                                    return runAggregationOnce(VocItemAggregation, {
                                        vocItemId,
                                        type,
                                        segmentId: null,
                                        allTime: true,
                                        timeSeriesConfig: null,
                                        revenueField,
                                        accountDisplayName,
                                        expandInsights: isExploreSetup,
                                    });
                                }

                                return item;
                            })
                            .catch((error) => {
                                throw error;
                            })
                            .finally(() => commit('setPromise', { key: `vocItem-${vocItemId}`, promise: null }));

                        commit('setPromise', { key: `vocItem-${vocItemId}`, promise });
                        newItem = await promise;
                    }
                    if (addToMap && type === 'idea') {
                        commit('setMap', {
                            map: {
                                ...state.map,
                                [vocItemId]: newItem,
                            },
                        });
                    } else if (addToMap) {
                        addVocItem(newItem);
                    }
                }

                const resolvedVocItem = state.map[vocItemId] || getVocItemById(vocItemId) || newItem;
                if (isEmpty(resolvedVocItem)) {
                    throw new Error('VocItem not found');
                }

                const vocItem = {
                    ...resolvedVocItem,
                    roadmapItemId,
                    isVisible: true,
                };
                const activeItems = keyBy(
                    values(state.blade.activeItems).map((item) => {
                        return {
                            ...item,
                            isVisible: false,
                        };
                    }),
                    'type',
                );

                commit('setBlade', {
                    activeItems: {
                        ...activeItems,
                        [vocItem.type]: vocItem,
                    },
                    openVotesTab,
                    openLinkedIdeasTab,
                    goToSuggestions,
                    voteFilter,
                    openingOnTopOfStackedBlade,
                    openingOnTopOfDifferentBlade,
                    openedFromRoadmap,
                    hasBeenPromotedToCurrentRoadmap,
                    persistOpenItem,
                    selectedCommentId,
                });

                if (getters.hasSuggestionPermissions) {
                    dispatch('setSuggestedItems', { item: vocItem });
                }

                return vocItem;
            } catch (error) {
                commit('setError', { error });

                throw error;
            } finally {
                commit('setLoadingBladeItem', { loading: false });
                cleanupVocItems();
            }
        },
        closeBlade ({ state, commit, getters, dispatch }, { leaveStackedBladeOpen = false, closeAll = false } = {}) {
            if (closeAll) {
                commit('setBlade', {
                    leaveStackedBladeOpen,
                });

                return;
            }

            const { [getters.bladeActiveItem?.type]: activeItem, ...hiddenActiveItem } = state.blade.activeItems;

            let newActiveItem = {};
            if (!isEmpty(hiddenActiveItem)) {
                newActiveItem = {
                    ...values(hiddenActiveItem)[0],
                    isVisible: true,
                };
            }

            commit('setBlade', {
                activeItems: {
                    ...(!isEmpty(newActiveItem) && { [newActiveItem.type]: newActiveItem }),
                },
                openLinkedIdeasTab: !isEmpty(newActiveItem),
                leaveStackedBladeOpen,
            });

            if (newActiveItem.type === 'feedback' && newActiveItem.numOfSuggestions > 0) {
                dispatch('setSuggestedItems', { item: newActiveItem });
            }
        },
        // This function will return the active item. It is called loadOne so it can be used by the groups module
        // to add the voc item to the group
        loadOne ({ getters }) {
            return getters.bladeActiveItem;
        },
        setCreateDrawerOpen ({ commit }, createDrawerOpen) {
            commit('setCreateDrawerOpen', { createDrawerOpen });
        },
        setAddVoteModalOpen ({ commit }, addVoteModalOpen) {
            commit('setAddVoteModalOpen', { addVoteModalOpen });
        },
        setShowStatusModalForCurrentSession ({ commit, state }) {
            commit('setShowStatusModalForCurrentSession', !state.showStatusModalForCurrentSession);
        },
        async loadInitialFormData ({ commit, state }, { reFetch = false, type = 'idea' } = {}) {
            if (!isEmpty(state.formData[type]) && !reFetch) {
                return;
            }

            try {
                commit('setFetching', { fetching: true });
                const formData = await fetchInitialFormData(type);

                commit('setFetching', { fetching: false });
                commit('setFormData', { type, formData });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        async createVocItem (
            { state, commit, dispatch, getters, rootState, rootGetters },
            { vocItem, acceptedSuggestions = [], loadBlade = true, storeInMap = true },
        ) {
            try {
                let createdItem = await createVocItem(vocItem);
                createdItem = {
                    ...createdItem,
                    vocItemTitle: vocItem.formData[0]?.content,
                    vocItemDescription: vocItem.formData[1]?.content,
                    kind: 'vocItem',
                };
                if (createdItem.type === 'idea') {
                    createdItem = {
                        ...createdItem,
                        voteCount: 0,
                        accountVoteCount: 0,
                        createdByName: createdItem.createdBy.user.username,
                    };
                    commit('incrementVocItemCount');
                    dispatch('loadAll', { reFetch: true });
                }

                const revenueField = get(rootState.subscription.settings, 'revenue.value', null);
                const accountDisplayName = get(rootState.subscription.settings, 'accountDisplayName.value', null);
                VocItemAggregation.subProductArea = createdItem.type === 'idea' ? VALIDATE : CAPTURE;
                const itemFromAgg = await runAggregationOnce(VocItemAggregation, {
                    vocItemId: createdItem.id,
                    appId: getters.appIdsForAggregation(createdItem),
                    type: createdItem.type,
                    segmentId: createdItem.type === 'idea' ? rootState.globalFilters.selectedSegmentId : null,
                    allTime: createdItem.type === 'idea' ? rootGetters['globalFilters/allTime'] : true,
                    timeSeriesConfig: createdItem.type === 'idea' ? rootGetters['globalFilters/timeSeriesSpec'] : null,
                    shouldFilterVotes: false,
                    revenueField,
                    accountDisplayName,
                });
                const formattedItem = { ...createdItem, ...itemFromAgg };

                if (formattedItem.labelIds?.length > 0) {
                    await dispatch('voc/labels/loadLabels', { reFetch: true }, { root: true });
                }

                if (vocItem.associatedItemIds?.length > 0) {
                    vocItem.associatedItemIds.forEach((assocId) => {
                        window.pendo?.track('feedback-linked-to-idea', {
                            suggested: acceptedSuggestions.includes(assocId),
                            context: `${formattedItem.type}-form`,
                        });
                    });

                    commit('updateAssociationsInMap', { createdItem: formattedItem });
                }
                if (storeInMap && createdItem.type === 'idea') {
                    commit('setMap', {
                        map: {
                            ...state.map,
                            [formattedItem.id]: formattedItem,
                        },
                    });
                } else if (storeInMap) {
                    const { addVocItem, cleanupVocItems } = useVocItems({ type: createdItem.type });
                    addVocItem(formattedItem);
                    cleanupVocItems();
                }

                if (formattedItem.type === 'idea' && loadBlade) {
                    dispatch('openBladeForItem', {
                        vocItemId: formattedItem.id,
                    });
                }

                return formattedItem;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateVocItem ({ state, commit, getters, dispatch }, { vocItemId, changes }) {
            try {
                const updatedItem = await updateVocItem(vocItemId, changes);
                const username = updatedItem.lastUpdatedBy?.user?.username;

                const { getVocItemById, updateVocItem: updateVocItemState, cleanupVocItems } = useVocItems({
                    type: updatedItem.type,
                });

                const existingItem =
                    state.blade.activeItems[updatedItem.type]?.id === updatedItem.id
                        ? state.blade.activeItems[updatedItem.type]
                        : state.map[updatedItem.id] || getVocItemById(updatedItem.id);
                const formattedUpdatedItem = {
                    ...existingItem,
                    ...updatedItem,
                    createdBy: existingItem?.createdBy ?? updatedItem?.createdBy,
                    ownedByUserId: updatedItem?.ownedByUser?.id,
                    lastUpdatedBy: {
                        username,
                    },
                    onBehalfOf: existingItem?.onBehalfOf,
                    ...(updatedItem.formData && {
                        vocItemTitle: updatedItem.formData[0]?.content,
                        vocItemDescription: updatedItem.formData[1]?.content,
                    }),
                    ...(getters.bladeActiveItem?.type === 'idea' && {
                        workaroundObj: {
                            ...updatedItem.workaround,
                            lastSentAt: updatedItem.workaroundNotificationLastSentAt,
                            lastSentByUser: updatedItem.workaroundNotificationLastSentByUser,
                        },
                    }),
                    ...(changes.highlight && {
                        highlight: {
                            ...existingItem?.highlight,
                            ...changes.highlight,
                        },
                    }),
                };

                if (changes.labels) await dispatch('voc/labels/loadLabels', { reFetch: true }, { root: true });

                delete formattedUpdatedItem.workaround;
                delete formattedUpdatedItem.workaroundNotificationLastSentAt;
                delete formattedUpdatedItem.workaroundNotificationLastSentByUser;

                if (state.map[updatedItem.id]) {
                    commit('setMap', {
                        map: {
                            ...state.map,
                            [updatedItem.id]: formattedUpdatedItem,
                        },
                    });
                } else if (getVocItemById(updatedItem.id)) {
                    updateVocItemState(updatedItem.id, formattedUpdatedItem);
                }
                cleanupVocItems();

                // if the item is open in the blade, update it in the blade map
                if (state.blade.activeItems[updatedItem.type]?.id === updatedItem.id) {
                    commit('setBladeActiveItems', {
                        activeItems: {
                            ...state.blade.activeItems,
                            [updatedItem.type]: formattedUpdatedItem,
                        },
                    });
                }

                // if the change is to title or description, check whether any feedback is open in the blade and update it's expanded items
                if (changes.formData && state.blade.activeItems.feedback?.associatedItemIds?.includes(updatedItem.id)) {
                    const associatedItemsExpanded = state.blade.activeItems.feedback.associatedItemsExpanded?.map(
                        (item) => {
                            if (item.id === updatedItem.id) {
                                return {
                                    ...item,
                                    ...formattedUpdatedItem,
                                };
                            }

                            return item;
                        },
                    );
                    const updatedFeedbackItem = {
                        ...state.blade.activeItems.feedback,
                        associatedItemsExpanded,
                    };

                    commit('setBladeActiveItems', {
                        activeItems: {
                            ...state.blade.activeItems,
                            feedback: updatedFeedbackItem,
                        },
                    });
                    if (state.map[updatedItem.id]) {
                        commit('setMap', {
                            map: {
                                ...state.map,
                                [state.blade.activeItems.feedback.id]: updatedFeedbackItem,
                            },
                        });
                    } else {
                        const { getVocItemById, updateVocItem, cleanupVocItems } = useVocItems({
                            type: updatedFeedbackItem.type,
                        });
                        if (getVocItemById(state.blade.activeItems.feedback.id)) {
                            updateVocItem(state.blade.activeItems.feedback.id, updatedFeedbackItem);
                        }
                        cleanupVocItems();
                    }
                }

                commit('incrementBladeChangeTrigger');
                if (state.blade.openingOnTopOfStackedBlade) {
                    EventBus.$emit('update-stacked-blade', formattedUpdatedItem);
                }

                return formattedUpdatedItem;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async loadVocItemCount ({ commit, state }, { reFetch = false, type = 'idea' } = {}) {
            if (type === state.vocItemCountType && !isNull(state.vocItemCount) && !reFetch) {
                return;
            }

            try {
                commit('setFetching', { fetching: true });
                commit('setVocItemCount', { vocItemCount: null });

                VocItemsCountAggregation.subProductArea = type === 'idea' ? VALIDATE : CAPTURE;
                const vocItemCount = (await runAggregationOnce(VocItemsCountAggregation, { type })).count;

                commit('setVocItemCount', { vocItemCount });
                commit('setVocItemCountType', { type });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async revokeVoteFromVocItem ({ state, commit, getters, rootState, rootGetters }, { vocItemId, voteId }) {
            try {
                await revokeVote(vocItemId, voteId);

                const currentItem = state.map[vocItemId] || getters.bladeActiveItem;
                const revenueField = get(rootState.subscription.settings, 'revenue.value', null);
                const accountDisplayName = get(rootState.subscription.settings, 'accountDisplayName.value', null);
                VocItemAggregation.subProductArea = currentItem.type === 'idea' ? VALIDATE : CAPTURE;
                const updatedItem = await runAggregationOnce(VocItemAggregation, {
                    vocItemId,
                    appId: getters.appIdsForAggregation(currentItem),
                    type: currentItem.type,
                    segmentId: currentItem.type === 'idea' ? rootState.globalFilters.selectedSegmentId : null,
                    allTime: currentItem.type === 'idea' ? rootGetters['globalFilters/allTime'] : true,
                    timeSeriesConfig: currentItem.type === 'idea' ? rootGetters['globalFilters/timeSeriesSpec'] : null,
                    shouldFilterVotes: false,
                    revenueField,
                    accountDisplayName,
                });

                if (state.map[vocItemId]) {
                    commit('setMap', {
                        map: {
                            ...state.map,
                            [updatedItem.id]: {
                                ...currentItem,
                                ...updatedItem,
                            },
                        },
                    });
                } else {
                    const { getVocItemById, updateVocItem, cleanupVocItems } = useVocItems({ type: currentItem.type });
                    if (getVocItemById(vocItemId)) {
                        updateVocItem(vocItemId, {
                            ...currentItem,
                            ...updatedItem,
                        });
                    }
                    cleanupVocItems();
                }
                if (state.blade.activeItems[updatedItem.type]?.id === updatedItem.id) {
                    commit('setBladeActiveItems', {
                        activeItems: {
                            ...state.blade.activeItems,
                            [updatedItem.type]: {
                                ...state.blade.activeItems[updatedItem.type],
                                ...updatedItem,
                            },
                        },
                    });
                }

                commit('incrementBladeChangeTrigger');
                if (state.blade.openingOnTopOfStackedBlade) {
                    EventBus.$emit('update-stacked-blade', {
                        ...currentItem,
                        ...updatedItem,
                    });
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        updateBladeActiveVocItem ({ state, commit, getters }, { changes }) {
            if (!getters.bladeActiveItem) return;
            commit('updateBladeActiveVocItem', { changes });
            if (state.map[getters.bladeActiveItem.id]) {
                commit('setMap', {
                    map: {
                        ...state.map,
                        [getters.bladeActiveItem.id]: getters.bladeActiveItem,
                    },
                });
            } else {
                const { getVocItemById, updateVocItem, cleanupVocItems } = useVocItems({
                    type: getters.bladeActiveItem.type,
                });
                if (getVocItemById(getters.bladeActiveItem.id)) {
                    updateVocItem(getters.bladeActiveItem.id, getters.bladeActiveItem);
                }
                cleanupVocItems();
            }

            if (values(changes).length === 1 && changes.numOfSuggestions) return;

            commit('incrementBladeChangeTrigger');
        },
        async addVote (
            { state, commit, getters, rootState, rootGetters },
            { vocItemId, vocItem, type = 'idea', onBehalfOfId, onBehalfOfType, strength, comment, accountId },
        ) {
            const { getVocItemById, updateVocItem, cleanupVocItems } = useVocItems({ type });
            try {
                const voteInfo = await addVote(vocItemId, onBehalfOfId, onBehalfOfType, strength, comment, accountId);

                const currentItem =
                    vocItem || state.map[vocItemId] || getVocItemById(vocItemId) || getters.bladeActiveItem;
                const revenueField = get(rootState.subscription.settings, 'revenue.value', null);
                const accountDisplayName = get(rootState.subscription.settings, 'accountDisplayName.value', null);
                VocItemAggregation.subProductArea = currentItem.type === 'idea' ? VALIDATE : CAPTURE;
                const updatedItem = await runAggregationOnce(VocItemAggregation, {
                    vocItemId,
                    appId: getters.appIdsForAggregation(currentItem),
                    type: currentItem.type,
                    segmentId: currentItem.type === 'idea' ? rootState.globalFilters.selectedSegmentId : null,
                    allTime: currentItem.type === 'idea' ? rootGetters['globalFilters/allTime'] : true,
                    timeSeriesConfig: currentItem.type === 'idea' ? rootGetters['globalFilters/timeSeriesSpec'] : null,
                    shouldFilterVotes: false,
                    revenueField,
                    accountDisplayName,
                });
                // the agg is nullifying associatedItemsExpanded
                updatedItem.associatedItemsExpanded = currentItem.associatedItemsExpanded;
                if (state.map[vocItemId]) {
                    commit('setMap', {
                        map: {
                            ...state.map,
                            [updatedItem.id]: {
                                ...currentItem,
                                ...updatedItem,
                            },
                        },
                    });
                } else if (getVocItemById(vocItemId)) {
                    updateVocItem(vocItemId, {
                        ...currentItem,
                        ...updatedItem,
                    });
                }
                if (state.blade.activeItems[updatedItem.type]?.id === updatedItem.id) {
                    commit('setBladeActiveItems', {
                        activeItems: {
                            ...state.blade.activeItems,
                            [updatedItem.type]: {
                                ...state.blade.activeItems[updatedItem.type],
                                ...updatedItem,
                            },
                        },
                    });
                }

                commit('incrementBladeChangeTrigger');
                if (state.blade.openingOnTopOfStackedBlade) {
                    EventBus.$emit('update-stacked-blade', {
                        ...currentItem,
                        ...updatedItem,
                    });
                }

                return voteInfo;
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                cleanupVocItems();
            }
        },
        async createAttachmentMedia ({ commit, dispatch }, { imageFiles, vocItemId }) {
            const failedUploadedFiles = [];
            // have to use a for loop waterfall due to race condition for forEach/.map with async.
            // currently looking into changing the BE to be more efficient
            for (const imageFile of imageFiles) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    await dispatch('createAndAttachImage', { imageFile, vocItemId });
                } catch (err) {
                    failedUploadedFiles.push(imageFile);
                    commit('setError', { err });
                }
            }

            return failedUploadedFiles;
        },
        async createAndAttachImage (__, { imageFile, vocItemId }) {
            try {
                const attachmentResp = await createAttachmentMedia(vocItemId, imageFile.name, imageFile.type);
                await gcsPost(attachmentResp.uploadUrl, imageFile, attachmentResp.formData);
                const vocItem = await addAttachmentToVocItem(vocItemId, attachmentResp.mediaFileId);

                return { id: attachmentResp.mediaFileId, vocItem };
            } catch (error) {
                throw error;
            }
        },
        attachLinksToVocItem ({ commit, dispatch }, { linkFiles, vocItemId }) {
            try {
                const linksData = linkFiles.map((linkFile) => {
                    return { linkUrl: linkFile.name, title: linkFile.title };
                });
                dispatch('updateVocItem', { vocItemId, changes: { linksData } });
            } catch (err) {
                commit('setError', { err });
            }
        },
        async createVocItemMedia ({ commit, dispatch, getters }, { imageFile, altText }) {
            try {
                const vocItemId = getters.bladeActiveItem.id;
                const bannerImageResp = await createVocItemMedia(vocItemId, 'bannerImage', { altText });

                await gcsPost(bannerImageResp.uploadUrl, imageFile, bannerImageResp.formData);
                const addBannerImageToVocItemResp = await updateVocItem(vocItemId, {
                    bannerImageId: bannerImageResp.mediaFileId,
                });

                dispatch('updateBladeActiveVocItem', {
                    changes: {
                        bannerImageId: bannerImageResp.mediaFileId,
                        lastUpdatedAt: addBannerImageToVocItemResp.lastUpdatedAt,
                        lastUpdatedBy: {
                            username: addBannerImageToVocItemResp.lastUpdatedBy.user.username,
                        },
                    },
                });
                const vocItemBannerImageId = getters.bladeActiveItem.bannerImageId;
                const bannerImageData = await fetchBannerImageUrl(vocItemId, vocItemBannerImageId);

                commit('setActiveBannerImage', { bannerImage: bannerImageData.url, altText: bannerImageData.altText || '' });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async fetchBannerImage ({ commit, getters }) {
            try {
                const vocItemBannerImageId = getters.bladeActiveItem.bannerImageId;
                const vocItemId = getters.bladeActiveItem.id;

                if (vocItemBannerImageId !== '') {
                    const bannerImageResp = await fetchBannerImageUrl(vocItemId, vocItemBannerImageId);
                    commit('setActiveBannerImage', { bannerImage: bannerImageResp.url, altText: bannerImageResp.altText || '' });
                } else {
                    commit('setActiveBannerImage', { bannerImage: '', altText: '' });
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async removeBannerImage ({ commit, dispatch, getters }) {
            try {
                const vocItemId = getters.bladeActiveItem.id;
                const addBannerImageToVocItemResp = await updateVocItem(vocItemId, { bannerImageId: '' });
                dispatch('updateBladeActiveVocItem', {
                    changes: {
                        bannerImageId: '',
                        lastUpdatedAt: addBannerImageToVocItemResp.lastUpdatedAt,
                        lastUpdatedBy: {
                            username: addBannerImageToVocItemResp.lastUpdatedBy.user.username,
                        },
                    },
                });
                commit('setActiveBannerImage', { bannerImage: '', altText: '' });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async updateBannerImageAltText ({ commit, getters }, { altText }) {
            try {
                const vocItemId = getters.bladeActiveItem.id;
                const { bannerImageId } = getters.bladeActiveItem;
                await updateBannerImageAltText(vocItemId, bannerImageId, altText);
                commit('setActiveBannerImageAltText', { altText });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async loadIdeaPollCount ({ commit, state }, { reFetch = false } = {}) {
            if (!isNull(state.ideaPollCount) && !reFetch) {
                return;
            }
            try {
                commit('setFetching', { fetching: true });
                const { count } = await runAggregationOnce(IdeaPollsCountAggregation, {});
                commit('setIdeaPollCount', { ideaPollCount: count });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        loadAttachmentsData ({ commit }, { vocItem }) {
            commit('resetAttachmentList');

            const attachmentCount = !!vocItem.attachmentIds?.length;
            if (attachmentCount) {
                vocItem.attachmentIds.forEach((attachmentId) => {
                    commit('addToAttachmentList', {
                        fileData: {
                            id: attachmentId,
                            type: 'image',
                            loading: true,
                        },
                    });

                    fetchAttachmentUrl(vocItem.id, attachmentId).then((attachment) => {
                        downloadImage({
                            url: attachment.url,
                            type: attachment.fileType,
                            name: attachment.fileName,
                        }).then((file) => {
                            commit('updateAttachmentList', {
                                fileData: {
                                    file,
                                    id: attachmentId,
                                    type: 'image',
                                    loading: false,
                                },
                            });
                        });
                    });
                });
            }

            const linkCount = !!vocItem.linksData?.length;
            if (linkCount) {
                vocItem.linksData.forEach((linkData) => {
                    commit('addToAttachmentList', {
                        fileData: {
                            file: {
                                name: linkData.linkUrl,
                                title: linkData.title,
                            },
                            type: 'link',
                            loading: false,
                            id: uuid(),
                        },
                    });
                });
            }
        },
        async removeVocItem ({ state, getters, commit, dispatch, rootState }, { vocItemId, type = 'idea' }) {
            try {
                await deleteVocItem(vocItemId);
                dispatch('loadAll', { reFetch: true });
                commit('reduceVocItemCount');
                commit('incrementBladeChangeTrigger');

                const { getVocItemById, removeVocItem, updateVocItem, cleanupVocItems } = useVocItems({ type });

                const vocItem = state.map[vocItemId] || getVocItemById(vocItemId);
                const vocItemType = vocItem?.type || getters.bladeActiveItem?.type || type;

                if (state.map[vocItemId]) {
                    const { [vocItemId]: item, ...newMap } = state.map;
                    commit('setMap', { map: newMap });
                } else if (vocItem) {
                    removeVocItem(vocItemId);
                }

                if (vocItem?.labelIds?.length > 0) {
                    await dispatch('voc/labels/loadLabels', { reFetch: true }, { root: true });
                }

                if (vocItemType === 'feedback' && rootState.savedViews.savedView) {
                    dispatch(
                        'savedViews/updateItemCount',
                        {
                            count: getters.listByType('feedback').length,
                            type: 'feedback',
                            id: rootState.savedViews.savedView.id,
                        },
                        { root: true },
                    );
                }

                const typeOfActiveItem = Object.keys(state.blade.activeItems).find(
                    (key) => state.blade.activeItems[key].id === vocItemId,
                );
                if (typeOfActiveItem) {
                    const { [typeOfActiveItem]: activeItem, ...hiddenActiveItem } = state.blade.activeItems;

                    let newActiveItem = {};
                    if (!isEmpty(hiddenActiveItem)) {
                        newActiveItem = {
                            ...values(hiddenActiveItem)[0],
                            isVisible: true,
                            ...(values(hiddenActiveItem)[0].associatedItemIds && {
                                associatedItemIds: values(hiddenActiveItem)[0].associatedItemIds.filter(
                                    (id) => id !== vocItemId,
                                ),
                            }),
                            ...(values(hiddenActiveItem)[0].associatedItemsExpanded && {
                                associatedItemsExpanded: values(hiddenActiveItem)[0].associatedItemsExpanded.filter(
                                    (expandedItem) => expandedItem.id !== vocItemId,
                                ),
                            }),
                        };

                        if (state.map[newActiveItem.id]) {
                            commit('setMap', {
                                map: {
                                    ...state.map,
                                    [newActiveItem.id]: newActiveItem,
                                },
                            });
                        } else if (getVocItemById(newActiveItem.id)) {
                            updateVocItem(newActiveItem.id, newActiveItem);
                        }
                    }
                    cleanupVocItems();

                    // allows events to fire before watcher removes item from the blade
                    setTimeout(() => {
                        commit('setBlade', {
                            activeItems: {
                                ...(!isEmpty(newActiveItem) && { [newActiveItem.type]: newActiveItem }),
                            },
                            openLinkedIdeasTab: !isEmpty(newActiveItem),
                        });
                    }, 0);

                    if (newActiveItem.type === 'feedback' && newActiveItem.numOfSuggestions > 0) {
                        dispatch('setSuggestedItems', { item: newActiveItem });
                    }
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async loadAll ({ commit, state }, { reFetch = false } = {}) {
            if (!isEmpty(state.allIdeas) && !reFetch) {
                return;
            }
            try {
                commit('setFetching', { fetching: true });
                const vocItems = await loadAllVocItems();
                commit('setAllIdeas', { vocItems });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setFetching', { fetching: false });
            }
        },
        async addToAttachmentList ({ dispatch, commit, state }, { attachmentFile, type, id, vocItem, persist = false }) {
            try {
                commit('addToAttachmentList', {
                    fileData: {
                        id,
                        type,
                        loading: true,
                    },
                });

                if (type === 'link' && persist) {
                    const linksData = state.attachmentList
                        .filter((link) => link.type === 'link' && !link.loading)
                        .map(({ file: link }) => {
                            return { linkUrl: link.name, title: link.title };
                        });

                    linksData.push({
                        linkUrl: attachmentFile.name,
                        title: attachmentFile.title,
                    });
                    const updatedVocItem = await updateVocItem(vocItem.id, { linksData });
                    dispatch('updateBladeActiveVocItem', {
                        changes: {
                            linksData,
                            lastUpdatedAt: updatedVocItem.lastUpdatedAt,
                            lastUpdatedBy: {
                                username: updatedVocItem.lastUpdatedBy.user?.username,
                            },
                        },
                    });
                }

                if (type === 'image' && persist) {
                    return ImageUploadQueue.addImage(async () => {
                        const createImageFile = await dispatch('createAndAttachImage', {
                            imageFile: attachmentFile,
                            vocItemId: vocItem.id,
                        });
                        const attachmentIds = [
                            ...(vocItem.attachmentIds?.length ? vocItem.attachmentIds : []),
                            createImageFile.id,
                        ];

                        dispatch('updateBladeActiveVocItem', {
                            changes: {
                                attachmentIds,
                                lastUpdatedAt: createImageFile.vocItem.lastUpdatedAt,
                                lastUpdatedBy: {
                                    username: createImageFile.vocItem.lastUpdatedBy.user?.username,
                                },
                            },
                        });

                        const newId = createImageFile.id;
                        const fileData = { file: attachmentFile, type, id, newId, loading: false };
                        commit('updateAttachmentList', { fileData });
                    }).catch((error) => {
                        commit('removeAttachmentFromList', { id });
                        throw error;
                    });
                }

                const fileData = { file: attachmentFile, type, id, loading: false };
                commit('updateAttachmentList', { fileData });
            } catch (error) {
                commit('removeAttachmentFromList', { id });
                throw error;
            }
        },
        removeAttachmentFromList ({ commit }, { id }) {
            commit('removeAttachmentFromList', { id });
        },
        resetAttachmentList ({ commit }) {
            commit('resetAttachmentList');
        },
        async deleteAttachment ({ commit, state, dispatch }, { id, vocItem }) {
            const fileData = state.attachmentList.find((attachment) => attachment.id === id);
            try {
                commit('updateAttachmentList', {
                    fileData: {
                        id,
                        loading: true,
                    },
                });
                await deleteAttachment(vocItem.id, id);
                commit('removeAttachmentFromList', { id });

                dispatch('updateBladeActiveVocItem', {
                    changes: {
                        attachmentIds: vocItem.attachmentIds.filter((attachment) => attachment !== id),
                    },
                });
            } catch (error) {
                commit('updateAttachmentList', { fileData });
                throw error;
            }
        },
        async deleteLink ({ commit, state, dispatch }, { id, vocItem }) {
            const fileData = state.attachmentList.find((attachment) => attachment.id === id);

            try {
                commit('updateAttachmentList', {
                    fileData: {
                        id,
                        loading: true,
                    },
                });
                const linksData = state.attachmentList
                    .filter((link) => link.type === 'link' && link.id !== id)
                    .map(({ file: link }) => {
                        return { linkUrl: link.name, title: link.title };
                    });

                await updateVocItem(vocItem.id, { linksData });
                commit('removeAttachmentFromList', { id });

                dispatch('updateBladeActiveVocItem', {
                    changes: {
                        linksData,
                    },
                });
            } catch (error) {
                commit('updateAttachmentList', { fileData });
                throw error;
            }
        },
        setLoadingLinkedIdeas ({ commit }, { isLoading }) {
            commit('setLoadingLinkedIdeas', { isLoading });
        },
        setLoadingLinkedJira ({ commit }, { isLoading }) {
            commit('setLoadingLinkedJira', { isLoading });
        },
        async setSuggestedItems ({ commit }, { item }) {
            commit('setLoadingSuggestions', { loading: true });

            try {
                VocItemSuggestionsForSingleItemAggregation.subProductArea = item.type === 'idea' ? VALIDATE : CAPTURE;
                const suggestions = await runAggregationOnce(VocItemSuggestionsForSingleItemAggregation, {
                    vocItemId: item.id,
                    type: item.type,
                });
                commit('setActiveSuggestions', { suggestions });
            } catch (error) {
                commit('setError', { error });
                throw error;
            } finally {
                commit('setLoadingSuggestions', { loading: false });
            }
        },
        setLoadingSuggestions ({ commit }, { loading }) {
            commit('setLoadingSuggestions', { loading });
        },
        async rejectSuggestion ({ commit }, { suggestionId }) {
            try {
                await rejectSuggestion({ suggestionId });
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async loadVocItemById ({ commit, state }, { vocItemId } = {}) {
            try {
                commit('setFetching', { fetching: true });
                const vocItem = await fetchVocItemData(vocItemId);

                commit('setMap', {
                    map: {
                        ...state.map,
                        [vocItem.id]: vocItem,
                    },
                });
                commit('setFetching', { fetching: false });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        removeSuggestion ({ commit, state }, { itemId }) {
            commit('setActiveSuggestions', {
                suggestions: state.blade.activeItemSuggestedItems.filter((suggestion) => suggestion.leftId !== itemId),
            });
        },
        async linkFeedbackToIdea (
            { state, commit, rootState, rootGetters, getters },
            { feedbackId, ideaId, suggested = false, context = 'unknown' },
        ) {
            try {
                await linkFeedbackToIdea({ feedbackId, ideaId });

                window.pendo.track('feedback-linked-to-idea', {
                    suggested,
                    context,
                });
                // skip this part for suggestions on feedback, as there is no revenue change
                if (!suggested || context !== 'feedback-blade') {
                    const currentItem = state.map[getters.bladeActiveItem?.id] || getters.bladeActiveItem;
                    const revenueField = get(rootState.subscription.settings, 'revenue.value', null);
                    const accountDisplayName = get(rootState.subscription.settings, 'accountDisplayName.value', null);
                    VocItemAggregation.subProductArea = currentItem.type === 'idea' ? VALIDATE : CAPTURE;
                    const updatedItem = await runAggregationOnce(VocItemAggregation, {
                        vocItemId: getters.bladeActiveItem?.id,
                        appId: getters.appIdsForAggregation(currentItem),
                        type: currentItem.type,
                        segmentId: currentItem.type === 'idea' ? rootState.globalFilters.selectedSegmentId : null,
                        allTime: currentItem.type === 'idea' ? rootGetters['globalFilters/allTime'] : true,
                        timeSeriesConfig:
                            currentItem.type === 'idea' ? rootGetters['globalFilters/timeSeriesSpec'] : null,
                        shouldFilterVotes: false,
                        revenueField,
                        accountDisplayName,
                    });
                    const formattedItem = {
                        ...currentItem,
                        ...updatedItem,
                        associatedItemsExpanded:
                            updatedItem.associatedItemsExpanded || currentItem.associatedItemsExpanded,
                        isVisible: true,
                    };
                    if (state.map[getters.bladeActiveItem?.id]) {
                        commit('setMap', {
                            map: {
                                ...state.map,
                                [updatedItem.id]: formattedItem,
                            },
                        });
                    } else {
                        const { getVocItemById, updateVocItem, cleanupVocItems } = useVocItems({
                            type: currentItem.type,
                        });
                        if (getVocItemById(getters.bladeActiveItem?.id)) {
                            updateVocItem(getters.bladeActiveItem.id, formattedItem);
                        }
                        cleanupVocItems();
                    }
                    if (state.blade.activeItems[formattedItem.type]?.id === formattedItem.id) {
                        commit('setBladeActiveItems', {
                            activeItems: {
                                ...state.blade.activeItems,
                                [formattedItem.type]: formattedItem,
                            },
                        });
                    }
                    if (state.blade.openingOnTopOfStackedBlade) EventBus.$emit('update-stacked-blade', formattedItem);
                }
                commit('incrementBladeChangeTrigger');
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async unlinkFeedbackFromIdea (
            { state, commit, rootState, rootGetters, getters },
            { feedbackId, ideaId, vocItem, source },
        ) {
            try {
                if (source === 'Insight') {
                    await unlinkInsightFromIdea({ feedbackId, ideaId });
                } else {
                    await unlinkFeedbackFromIdea({ feedbackId, ideaId });
                }

                const currentItem = vocItem || state.map[getters.bladeActiveItem?.id] || getters.bladeActiveItem;
                const revenueField = get(rootState.subscription.settings, 'revenue.value', null);
                const accountDisplayName = get(rootState.subscription.settings, 'accountDisplayName.value', null);
                VocItemAggregation.subProductArea = currentItem.type === 'idea' ? VALIDATE : CAPTURE;
                const updatedItem = await runAggregationOnce(VocItemAggregation, {
                    vocItemId: currentItem?.id,
                    appId: getters.appIdsForAggregation(currentItem),
                    type: currentItem.type,
                    segmentId: currentItem.type === 'idea' ? rootState.globalFilters.selectedSegmentId : null,
                    allTime: currentItem.type === 'idea' ? rootGetters['globalFilters/allTime'] : true,
                    timeSeriesConfig: currentItem.type === 'idea' ? rootGetters['globalFilters/timeSeriesSpec'] : null,
                    shouldFilterVotes: false,
                    revenueField,
                    accountDisplayName,
                });
                if (state.map[currentItem?.id]) {
                    commit('setMap', {
                        map: {
                            ...state.map,
                            [updatedItem.id]: {
                                ...currentItem,
                                ...updatedItem,
                                associatedItemsExpanded:
                                    updatedItem.associatedItemsExpanded || currentItem.associatedItemsExpanded,
                            },
                        },
                    });
                } else {
                    const { getVocItemById, updateVocItem, cleanupVocItems } = useVocItems({ type: currentItem.type });
                    if (getVocItemById(getters.bladeActiveItem?.id)) {
                        updateVocItem(updatedItem.id, {
                            ...currentItem,
                            ...updatedItem,
                            associatedItemsExpanded:
                                updatedItem.associatedItemsExpanded || currentItem.associatedItemsExpanded,
                        });
                    }
                    cleanupVocItems();
                }
                if (state.blade.activeItems[updatedItem.type]?.id === updatedItem.id) {
                    commit('setBladeActiveItems', {
                        activeItems: {
                            ...state.blade.activeItems,
                            [updatedItem.type]: {
                                ...state.blade.activeItems[updatedItem.type],
                                ...updatedItem,
                                associatedItemsExpanded:
                                    updatedItem.associatedItemsExpanded || currentItem.associatedItemsExpanded,
                            },
                        },
                    });
                }

                commit('incrementBladeChangeTrigger');
                if (state.blade.openingOnTopOfStackedBlade) {
                    EventBus.$emit('update-stacked-blade', {
                        ...currentItem,
                        ...updatedItem,
                    });
                }
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        setVocItems ({ commit, state }, { vocItems, type = 'idea' }) {
            const storedVocItems = values(state.map).filter((item) => item.type !== type);
            const newVocItems = [...storedVocItems, ...vocItems];

            commit('setMap', { map: keyBy(newVocItems, 'id') });

            const activeItems = values(state.blade.activeItems);
            if (activeItems.length > 0) {
                const updatedActiveItems = activeItems.map((item) => {
                    const matchingVocItem = vocItems.find((vocItem) => vocItem.id === item.id);
                    if (matchingVocItem) {
                        return {
                            ...item,
                            ...matchingVocItem,
                            associatedItemsExpanded: item.associatedItemsExpanded?.filter((expandedItem) => {
                                return matchingVocItem.associatedItemIds?.includes(expandedItem.id);
                            }),
                        };
                    }

                    return item;
                });

                commit('setBladeActiveItems', {
                    activeItems: keyBy(updatedActiveItems, 'type'),
                });
            }
        },
        updateVocItemInMap ({ commit, state }, { vocItem }) {
            if (state.map[vocItem.id]) {
                commit('setMap', { map: { ...state.map, [vocItem.id]: vocItem } });
            } else {
                const { getVocItemById, updateVocItem, cleanupVocItems } = useVocItems({ type: vocItem.type });
                if (getVocItemById(vocItem.id)) {
                    updateVocItem(vocItem.id, vocItem);
                }
                cleanupVocItems();
            }
        },
        setContext ({ commit }, { context }) {
            commit('setContext', { context });
        },
        updateSingleItemLinkedItems ({ commit, state, getters }, { id, linkedItem, lastUpdatedAt }) {
            if (getters.bladeHiddenActiveItem?.id === id) {
                commit('updateBladeHiddenVocItemLinkedItems', { linkedItem, lastUpdatedAt });
            }
            if (state.map[id]) {
                commit('updateLinkedItemsInMap', { id, linkedItem, lastUpdatedAt });
            }
            commit('incrementBladeChangeTrigger');
        },
        async uploadCSV ({ commit }, { file }) {
            try {
                const bucketResp = await createCSVBucket();
                const assignedFileName = bucketResp.url.split('/').pop();
                await gcsPost(bucketResp.uploadUrl, file, bucketResp.formData);
                const uploadResp = await processCSV({ name: assignedFileName });

                return {
                    data: uploadResp.rows,
                    assignedFileName,
                };
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },

        async bulkLinkFeedbackToIdea ({ commit, state }, { feedbackItems, insightIds, idea }) {
            try {
                const bulkLinkResponse = await bulkLinkFeedbackToIdea({
                    ideaId: idea.id,
                    feedbackIds: feedbackItems.map((feedbackItem) => feedbackItem.id),
                    insightIds,
                });
                if (feedbackItems.length && bulkLinkResponse.feedback.FailedCount === feedbackItems.length) {
                    throw new Error('Failed to link any feedback items');
                }
                if (insightIds.length && bulkLinkResponse.insights.FailedCount === insightIds.length) {
                    throw new Error('Failed to link any insights');
                }

                const mapUpdates = {};

                feedbackItems
                    .filter(
                        (feedbackItem) =>
                            (!Array.isArray(feedbackItem.associatedItemIds) ||
                                !feedbackItem.associatedItemIds.includes(idea.id)) &&
                            !bulkLinkResponse.FailedChildren?.includes(feedbackItem.id),
                    )
                    .forEach((feedbackItem) => {
                        mapUpdates[feedbackItem.id] = {
                            ...feedbackItem,
                            associatedItemIds: feedbackItem.associatedItemIds
                                ? [...feedbackItem.associatedItemIds, idea.id]
                                : [idea.id],
                            associatedItemsExpanded: feedbackItem.associatedItemsExpanded
                                ? [...feedbackItem.associatedItemsExpanded, idea]
                                : [idea],
                        };
                    });
                if (getExploreStatus() !== EXPLORE_STATUSES.READY) {
                    commit('setMap', { map: { ...state.map, ...mapUpdates } });
                } else {
                    const { addVocItems: updateVocItems, cleanupVocItems } = useVocItems({ type: 'feedback' });
                    updateVocItems(Object.values(mapUpdates));

                    cleanupVocItems();
                }

                return bulkLinkResponse;
            } catch (err) {
                throw err;
            }
        },
        async getVocItemUpdateEmail ({ commit, getters }, { vocItemId, allUpdates = false }) {
            try {
                let vocItemUpdateEmail;
                let updateLog;

                if (getters.shareUpdateV2Visible) {
                    updateLog = await getVocItemEmailUpdates(vocItemId);

                    vocItemUpdateEmail = updateLog
                        .filter((update) => update.campaignStatus === 'sent' || update.campaignStatus === 'public')
                        .sort((a, b) => b.firstSent - a.firstSent)[0];
                } else {
                    vocItemUpdateEmail = await getUpdateEmail(vocItemId);
                }

                commit('setLastVocItemUpdateEmail', vocItemUpdateEmail);

                return allUpdates ? updateLog : vocItemUpdateEmail;
            } catch (error) {
                commit('setError', { error });
                throw error;
            }
        },
        async getVocItemListWithSourceIdentifier ({ commit }, { source }) {
            await runAggregationOnce(VocItemListWithSourceIdentifierAggregation, { type: 'feedback', source }).then(
                (rows) => {
                    rows.forEach((pair) => {
                        commit('addResponseToResponsesAlreadySentToFeedback', {
                            sourceIdentifier: pair.pollResponseId,
                            vocItemId: pair.vocItemId,
                        });
                    });
                },
            );
        },
    },
    getters: {
        formData: (state) => (type = 'idea') => state.formData[type],
        createdById: (_state, getters) =>
            getters.bladeActiveItem?.createdBy?.id || getters.bladeActiveItem?.createdBy?.user?.id,
        canCreateVocItems: () =>
            $canSub()
                .entity('vocItemIdea')
                .action('create'),
        canSubmitFeedback: () =>
            $canSub()
                .entity('vocItemFeedback')
                .action('create'),
        canEditVocItems: () =>
            $canSub()
                .entity('vocItem')
                .action('edit'),
        canEditFeedbackRestricted: () =>
            $canSub()
                .entity('vocItemFeedback')
                .action('edit'),
        canAddVotes: () =>
            $canSub()
                .entity('vote')
                .action('create'),
        canLinkItems: () =>
            $canSub()
                .entity('vocItemAssociation')
                .action('create'),
        canRemoveLinkedIdeas: () =>
            $canSub()
                .entity('vocItemAssociation')
                .action('remove'),
        ownsCurrentVocItem: (_state, getters, rootState) => {
            const userId = rootState.auth.user.id;
            const { createdById } = getters;

            return userId === createdById;
        },
        canEditCurrentVocItem: (_state, getters) => {
            return (
                (getters.canEditFeedbackRestricted &&
                    getters.ownsCurrentVocItem &&
                    getters.bladeActiveItem?.type === 'feedback') ||
                getters.canEditVocItems
            );
        },
        list: (state) => values(state.map),
        listByType: (state) => (type = 'idea') => values(state.map).filter((item) => item.type === type),
        listCount: (state) => values(state.map).length,
        activeSuggestedItemIds: (state, getters) => {
            if (getters.bladeActiveItem?.type === 'idea') {
                return state.blade.activeItemSuggestedItems.map((suggestion) => suggestion.rightId);
            }
            if (getters.bladeActiveItem?.type === 'feedback') {
                return state.blade.activeItemSuggestedItems.map((suggestion) => suggestion.leftId);
            }

            return [];
        },
        hasSuggestionPermissions: (_state, getters, _rootState, rootGetters) => {
            return getters.canLinkItems && rootGetters['subscription/getFeatureFlag']('enableGoogleDiscoverAI');
        },
        bladeActiveItem: (state) => values(state.blade.activeItems).find((item) => item.isVisible),
        bladeHiddenActiveItem: (state) => values(state.blade.activeItems).find((item) => !item.isVisible),
        vocItemById: (state) => (id) => state.map[id],
        bladeHasMultipleActiveItems: (state) => Object.keys(state.blade.activeItems).length > 1,
        activeItemPromotedRoadmapItems: (state) => cloneDeep(state.blade.activeItemPromotedRoadmapItems),
        appIdsForAggregation: () => (item) =>
            flatten(item.appIds.map((appId) => AppsService.getAppAggIdsFromOption(appId))),
        listenAppOptions: (state, getters, rootState, rootGetters) =>
            AppsService.sortApps(rootGetters['apps/onlyIntegratedApps']),
        shareUpdateV2Visible: (_state, getters, _rootState, rootGetters) => {
            return (
                getters.canEditVocItems &&
                rootGetters['productLines/hasProductLinePurchased']('voc', 'email') &&
                rootGetters['subscription/getFeatureFlag']('vocEmail')
            );
        },
        loadingLinkedJiraIssues: (state) => state.loadingLinkedJira,
        isBulkActionInProgress: (state) => state.isBulkActionInProgress,
    },
};

export default vocItems;
